import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";
import {
  getColorForInterpretation,
  getRenderValue,
  getValueAndInterpretation,
  renderLabReferenceRange,
} from "../../../shared-components/fhir-sections/labs/shared";
import {
  getObservationCode as getObservationCodeForGrouping,
  getObservationDate,
  groupResourcesByKey,
  ObservationSeries,
} from "../../table-transforms/series-utils";

type ChartSeries = {
  name: string;
  data: Array<{ date: string; value: number }>;
  color?: string;
};

/** Get hover color for interpretation - darker shade of the same color */
function getHoverColorForInterpretation(interpretation: string | undefined): string | undefined {
  switch (interpretation) {
    case "low":
      return "rgba(40, 120, 175, 0.35)"; // Darker blue
    case "high":
      return "rgba(175, 70, 100, 0.35)"; // Darker red
    case "abnormal":
      return "rgba(175, 70, 100, 0.35)"; // Darker red
    case "normal":
      return "rgba(55, 175, 70, 0.35)"; // Darker green
    default:
      return undefined;
  }
}

export type LabRowData = {
  id: string;
  observation: string;
  value: string;
  interpretation: string;
  referenceRange: string;
  date: string;
  originalData: ObservationSeries;
  backgroundColor?: string;
  hoverColor?: string;
  chartSeries: ChartSeries[];
};

function extractObservationData(series: ObservationSeries): Array<{ date: string; value: number }> {
  const data: Array<{ date: string; value: number }> = [];

  for (const resource of series.resources) {
    const date = getObservationDate(resource);
    if (!date || date === "-") continue;

    const value = resource.valueQuantity?.value;
    if (value === undefined) continue;

    data.push({ date, value });
  }

  return data;
}

export function getLabRows({ bundle }: { bundle: FhirBundleSdk }): LabRowData[] {
  // Get only lab observations (category = "laboratory")
  const observations = bundle.getObservations().filter(observation => {
    return (
      observation.category?.some(cat =>
        cat.coding?.some(coding => coding.code?.toLowerCase() === "laboratory")
      ) ?? false
    );
  });

  // Group observations by code into series
  const observationSeries = groupResourcesByKey(
    observations,
    getObservationCodeForGrouping,
    getObservationDate,
    "Observation"
  );

  // Convert series to row data
  return observationSeries.map(series => {
    const latestObservation = series.resources[series.resources.length - 1]; // Last in array is newest

    if (!latestObservation) {
      throw new Error("Series should always have at least one resource");
    }

    const { value, unit, interpretation, referenceRange } =
      getValueAndInterpretation(latestObservation);

    const observationName =
      latestObservation.code?.coding?.[0]?.display || latestObservation.code?.text || "-";

    return {
      id: `series-${series.code}`,
      observation: observationName,
      value: getRenderValue(value, unit),
      interpretation: interpretation || "-",
      referenceRange: renderLabReferenceRange(referenceRange, unit),
      date: formatDate(series.latestDate),
      originalData: series,
      backgroundColor: getColorForInterpretation(interpretation),
      hoverColor: getHoverColorForInterpretation(interpretation),
      chartSeries: [
        {
          name: observationName,
          data: extractObservationData(series),
        },
      ],
    };
  });
}

function formatDate(date: string | undefined): string {
  if (!date || date === "-") return "-";
  try {
    const parsed = dayjs(date);
    if (!parsed.isValid()) return "-";
    return parsed.format(ISO_DATE);
  } catch {
    return "-";
  }
}
